import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import {
  Plus,
  DollarSign,
  Clock,
  CheckCircle,
  XCircle,
  RefreshCw,
  Search,
  Banknote,
  TrendingUp,
  Calendar,
  User,
  FileText,
} from 'lucide-react';

function PartnerPayoutsPage() {
  const [payouts, setPayouts] = useState([]);
  const [summary, setSummary] = useState({});
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('all');

  // Create payout dialog
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [createData, setCreateData] = useState({
    partner_id: '',
    amount: '',
    currency: 'USD',
    period_start: '',
    period_end: '',
    notes: '',
  });
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState('');

  // Mark paid / cancel dialogs
  const [showMarkPaidDialog, setShowMarkPaidDialog] = useState(false);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [selectedPayout, setSelectedPayout] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [payoutsRes, summaryRes, partnersRes] = await Promise.all([
        axios.get(`${API}/superadmin/partner-payouts`, { withCredentials: true }),
        axios.get(`${API}/superadmin/partner-payouts/summary`, { withCredentials: true }),
        axios.get(`${API}/superadmin/partners`, { withCredentials: true }),
      ]);
      setPayouts(payoutsRes.data || []);
      setSummary(summaryRes.data || {});
      setPartners(partnersRes.data || []);
    } catch (error) {
      console.error('Failed to fetch payouts:', error);
      toast.error('Failed to load payouts');
    } finally {
      setLoading(false);
    }
  };

  const handleCreatePayout = async (e) => {
    e.preventDefault();
    setCreateError('');

    if (!createData.partner_id) {
      setCreateError('Please select a partner');
      return;
    }
    if (!createData.amount || parseFloat(createData.amount) <= 0) {
      setCreateError('Please enter a valid amount');
      return;
    }

    setCreating(true);
    try {
      await axios.post(`${API}/superadmin/partner-payouts/create`, {
        ...createData,
        amount: parseFloat(createData.amount),
      }, { withCredentials: true });
      toast.success('Payout created successfully');
      setShowCreateDialog(false);
      setCreateData({ partner_id: '', amount: '', currency: 'USD', period_start: '', period_end: '', notes: '' });
      fetchAll();
    } catch (error) {
      setCreateError(error.response?.data?.detail || 'Failed to create payout');
    } finally {
      setCreating(false);
    }
  };

  const handleMarkPaid = async () => {
    if (!selectedPayout) return;
    setActionLoading(true);
    try {
      await axios.post(`${API}/superadmin/partner-payouts/${selectedPayout.payout_id}/mark-paid`, {}, { withCredentials: true });
      toast.success('Payout marked as paid');
      setShowMarkPaidDialog(false);
      setSelectedPayout(null);
      fetchAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to mark as paid');
    } finally {
      setActionLoading(false);
    }
  };

  const handleCancel = async () => {
    if (!selectedPayout) return;
    setActionLoading(true);
    try {
      await axios.post(`${API}/superadmin/partner-payouts/${selectedPayout.payout_id}/cancel`, {}, { withCredentials: true });
      toast.success('Payout cancelled');
      setShowCancelDialog(false);
      setSelectedPayout(null);
      fetchAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel payout');
    } finally {
      setActionLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric', month: 'short', day: 'numeric'
    });
  };

  const formatCurrency = (amount, currency = 'USD') => {
    return `${currency} ${(amount || 0).toFixed(2)}`;
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending':
        return <Badge className="bg-amber-100 text-amber-700"><Clock className="w-3 h-3 mr-1" />Pending</Badge>;
      case 'paid':
        return <Badge className="bg-green-100 text-green-700"><CheckCircle className="w-3 h-3 mr-1" />Paid</Badge>;
      case 'cancelled':
        return <Badge className="bg-red-100 text-red-700"><XCircle className="w-3 h-3 mr-1" />Cancelled</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const filteredPayouts = payouts.filter(p => {
    const matchesSearch = !searchTerm ||
      p.partner_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.payout_id?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesTab = activeTab === 'all' || p.status === activeTab;
    return matchesSearch && matchesTab;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="partner-payouts-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Partner Payouts</h1>
          <p className="text-gray-500">Manage partner payments and payout history</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchAll}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
          <Button
            onClick={() => { setCreateError(''); setShowCreateDialog(true); }}
            className="bg-indigo-600 hover:bg-indigo-700"
            data-testid="create-payout-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Create Payout
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-amber-100 rounded-lg">
                <Clock className="w-6 h-6 text-amber-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Pending</p>
                <p className="text-2xl font-bold text-amber-700" data-testid="summary-pending">
                  ${(summary.total_pending || 0).toFixed(2)}
                </p>
                <p className="text-xs text-gray-400">{summary.pending_count || 0} payouts</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-green-100 rounded-lg">
                <CheckCircle className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Total Paid</p>
                <p className="text-2xl font-bold text-green-700" data-testid="summary-paid">
                  ${(summary.total_paid || 0).toFixed(2)}
                </p>
                <p className="text-xs text-gray-400">{summary.paid_count || 0} payouts</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-red-100 rounded-lg">
                <XCircle className="w-6 h-6 text-red-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Cancelled</p>
                <p className="text-2xl font-bold text-red-700" data-testid="summary-cancelled">
                  ${(summary.total_cancelled || 0).toFixed(2)}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-indigo-100 rounded-lg">
                <TrendingUp className="w-6 h-6 text-indigo-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Total Payouts</p>
                <p className="text-2xl font-bold" data-testid="summary-total">{summary.total_count || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search & Tabs */}
      <div className="flex items-center gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input
            placeholder="Search by partner name or payout ID..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
            data-testid="payout-search"
          />
        </div>
      </div>

      {/* Payouts Table with Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="all" data-testid="tab-all">All ({payouts.length})</TabsTrigger>
          <TabsTrigger value="pending" data-testid="tab-pending">
            Pending ({payouts.filter(p => p.status === 'pending').length})
          </TabsTrigger>
          <TabsTrigger value="paid" data-testid="tab-paid">
            Paid ({payouts.filter(p => p.status === 'paid').length})
          </TabsTrigger>
          <TabsTrigger value="cancelled" data-testid="tab-cancelled">
            Cancelled ({payouts.filter(p => p.status === 'cancelled').length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value={activeTab} className="mt-4">
          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Payout ID</TableHead>
                    <TableHead>Partner</TableHead>
                    <TableHead>Amount</TableHead>
                    <TableHead>Period</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Created</TableHead>
                    <TableHead>Paid At</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredPayouts.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} className="text-center py-12 text-gray-500">
                        <Banknote className="w-10 h-10 mx-auto mb-2 text-gray-300" />
                        <p>No payouts found</p>
                        <p className="text-sm">Create a payout to get started</p>
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredPayouts.map((payout) => (
                      <TableRow key={payout.payout_id} data-testid={`payout-row-${payout.payout_id}`}>
                        <TableCell>
                          <code className="text-xs bg-gray-100 px-2 py-1 rounded">{payout.payout_id}</code>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <User className="w-4 h-4 text-gray-400" />
                            <span className="font-medium">{payout.partner_name || 'Unknown'}</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <span className="font-semibold text-gray-900">
                            {formatCurrency(payout.amount, payout.currency)}
                          </span>
                        </TableCell>
                        <TableCell>
                          <div className="text-sm text-gray-600">
                            {payout.period_start && payout.period_end ? (
                              <span>{formatDate(payout.period_start)} - {formatDate(payout.period_end)}</span>
                            ) : (
                              <span className="text-gray-400">-</span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>{getStatusBadge(payout.status)}</TableCell>
                        <TableCell className="text-sm text-gray-600">{formatDate(payout.created_at)}</TableCell>
                        <TableCell className="text-sm text-gray-600">{formatDate(payout.paid_at)}</TableCell>
                        <TableCell className="text-right">
                          {payout.status === 'pending' && (
                            <div className="flex justify-end gap-1">
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-green-600 hover:text-green-700 hover:bg-green-50"
                                onClick={() => { setSelectedPayout(payout); setShowMarkPaidDialog(true); }}
                                data-testid={`mark-paid-${payout.payout_id}`}
                              >
                                <CheckCircle className="w-4 h-4 mr-1" />
                                Mark Paid
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                onClick={() => { setSelectedPayout(payout); setShowCancelDialog(true); }}
                                data-testid={`cancel-${payout.payout_id}`}
                              >
                                <XCircle className="w-4 h-4 mr-1" />
                                Cancel
                              </Button>
                            </div>
                          )}
                          {payout.status !== 'pending' && (
                            <span className="text-xs text-gray-400">-</span>
                          )}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Create Payout Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Banknote className="w-5 h-5 text-indigo-600" />
              Create Payout
            </DialogTitle>
            <DialogDescription>Create a new payout record for a partner</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreatePayout} className="space-y-4">
            <div className="space-y-2">
              <Label>Partner *</Label>
              <Select value={createData.partner_id} onValueChange={(v) => setCreateData({ ...createData, partner_id: v })}>
                <SelectTrigger data-testid="create-payout-partner">
                  <SelectValue placeholder="Select partner..." />
                </SelectTrigger>
                <SelectContent>
                  {partners.filter(p => p.status === 'active').map((p) => (
                    <SelectItem key={p.partner_id} value={p.partner_id}>
                      <div className="flex items-center gap-2">
                        <span>{p.name}</span>
                        <span className="text-xs text-gray-400">({p.referral_code})</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Amount *</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0.01"
                  value={createData.amount}
                  onChange={(e) => setCreateData({ ...createData, amount: e.target.value })}
                  placeholder="0.00"
                  required
                  data-testid="create-payout-amount"
                />
              </div>
              <div className="space-y-2">
                <Label>Currency</Label>
                <Select value={createData.currency} onValueChange={(v) => setCreateData({ ...createData, currency: v })}>
                  <SelectTrigger data-testid="create-payout-currency">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="USD">USD</SelectItem>
                    <SelectItem value="EUR">EUR</SelectItem>
                    <SelectItem value="GBP">GBP</SelectItem>
                    <SelectItem value="INR">INR</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Period Start</Label>
                <Input
                  type="date"
                  value={createData.period_start}
                  onChange={(e) => setCreateData({ ...createData, period_start: e.target.value })}
                  data-testid="create-payout-period-start"
                />
              </div>
              <div className="space-y-2">
                <Label>Period End</Label>
                <Input
                  type="date"
                  value={createData.period_end}
                  onChange={(e) => setCreateData({ ...createData, period_end: e.target.value })}
                  data-testid="create-payout-period-end"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea
                value={createData.notes}
                onChange={(e) => setCreateData({ ...createData, notes: e.target.value })}
                placeholder="Payment notes (optional)"
                rows={2}
                data-testid="create-payout-notes"
              />
            </div>
            {createError && (
              <p className="text-sm text-red-600 bg-red-50 p-2 rounded" data-testid="create-payout-error">{createError}</p>
            )}
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowCreateDialog(false)}>Cancel</Button>
              <Button type="submit" disabled={creating} className="bg-indigo-600 hover:bg-indigo-700" data-testid="create-payout-submit">
                {creating ? 'Creating...' : 'Create Payout'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Mark Paid Confirmation */}
      <AlertDialog open={showMarkPaidDialog} onOpenChange={setShowMarkPaidDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Mark Payout as Paid?</AlertDialogTitle>
            <AlertDialogDescription>
              Confirm payment of <strong>{formatCurrency(selectedPayout?.amount, selectedPayout?.currency)}</strong> to <strong>{selectedPayout?.partner_name}</strong>.
              This action will update the partner's paid earnings.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={actionLoading}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleMarkPaid}
              disabled={actionLoading}
              className="bg-green-600 hover:bg-green-700"
            >
              {actionLoading ? 'Processing...' : 'Confirm Paid'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Cancel Payout Confirmation */}
      <AlertDialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Cancel Payout?</AlertDialogTitle>
            <AlertDialogDescription>
              Cancel the payout of <strong>{formatCurrency(selectedPayout?.amount, selectedPayout?.currency)}</strong> for <strong>{selectedPayout?.partner_name}</strong>?
              This will reverse the pending earnings.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={actionLoading}>Keep</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleCancel}
              disabled={actionLoading}
              className="bg-red-600 hover:bg-red-700"
            >
              {actionLoading ? 'Cancelling...' : 'Cancel Payout'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

export default PartnerPayoutsPage;
